import { Box, Button, Typography, useTheme } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { CustomGridToolbar } from "../../../utils/dataGridCustom";
import { tokens } from "../../../theme";
import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import {getCenterTodayStudents, updateStudentAttendance } from "../../../services/center-manager-services/getStudentData";
import Loader from "../../global/Loader";
import {pageSizeOptions, pageSize, page} from "../../../utils/paginationConfig";
import { putLogoutAllStudents } from "../../../services/center-manager-services/allStudentLogoutService";
import { saveAs } from "file-saver";
import { escapeCSVField } from "../../../utils/utils";

const DailyStudentOverview = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const [centerCode, setCenterCode] = useState("");
  const [data, setData] = useState([]);
  const [selectedData, setSelectedData] = useState({});
  const [paginationModel, setPaginationModel] = useState({page: page, pageSize: pageSize});
  const [rowCount, setRowCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);

  const headerData = ["Student Id", "Name", "PC Id", "Login Time", "Logout Time"];

  useEffect(() => {
    const centerId = localStorage.getItem("CenterCode");
    setCenterCode(centerId);

    const fetchData = async () => {
      try {
        await handleTableData(centerId, page, pageSize);
      } catch (error) {
        toast.error("Error fetching data");
      }
    };
    fetchData();
  }, []);


  const handleTableData = async (
    centerCode, 
    page = paginationModel.page,
    limit = paginationModel.pageSize
  ) => {
    setIsLoading(true);
    setPaginationModel({ page: page, pageSize: limit });

    try {
      const obj = { page, limit };
      const response = await getCenterTodayStudents(centerCode, obj);
      setData(response.data.data);
      setRowCount(response.data.totalCount ?? 0);
    } catch (error) {
      console.log(error);
      toast.error("Please refresh");
    }

    setIsLoading(false);
  };

  const handlePaginationModelChange = (newModel) => {
    const resetPage = newModel.pageSize !== paginationModel.pageSize;
    const updatedModel = {
      ...newModel,
      page: resetPage ? 0 : newModel.page,
    };

    setPaginationModel(updatedModel);
    handleTableData(centerCode, updatedModel.page, updatedModel.pageSize);
  };

  const handleExportCSV = async () => {
    try {
      const response = await getCenterTodayStudents(centerCode, {
        page: 0,
        limit: rowCount,
      });

      const data = response.data.data;
      const csvRows = data.map((row) =>
        [
          escapeCSVField(row.studentCode),
          escapeCSVField(row.name),
          escapeCSVField(row.pcCode),
          escapeCSVField(row.loginTime),
          escapeCSVField(row.logoutTime)
        ].join(",")
      );

      const csvContent = [headerData.join(","), ...csvRows].join("\n");

      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      saveAs(blob, `pc_performance_stats.csv`);
    } catch (error) {
      console.error("CSV Export Failed:", error);
      toast.error("Failed to export CSV.");
    }
  };

  const handleUpdate = async (id) => {
    try {
      const selectedData = data.find(
        (element) => element.attendanceCode === id
      );
      setSelectedData(selectedData);
      const response = await updateStudentAttendance(
        selectedData.attendanceCode
      );
      if (response.data.o_sql_msg === "success") {
        toast.success("Successfully logged out the student.");
      }
    } catch (error) {
      toast.error("Something went wrong while login out the student");
      console.log(error);
    }
  };

  const handleAllLogout = async () => {
    try {
      const centerId = localStorage.getItem("CenterCode");
      const response = await putLogoutAllStudents(centerId);

      if (response.data.o_sql_msg === "success") {
        toast.success("Successfully logged out all of the students.");
        setTimeout(() => {
          window.location.reload();
        }, 1000);
      }
      else{
        toast.error("Something went wrong while login out students");
      }
    } catch (error) {
      toast.error("Something went wrong while login out students");
      console.log(error);
    }
  };

  const columns = [
    {
      field: "studentCode",
      headerName: headerData[0],
      flex: 3,
      cellClassName: "name-column--cell",
    },
    {
      field: "name",
      headerName: headerData[1],
      flex: 4,
      renderCell: (params) => (
        <Box style={{ textTransform: "uppercase" }}>{params.value}</Box>
      ),
    },
    {
      field: "pcCode",
      headerName: headerData[2],
      flex: 3,
      cellClassName: "name-column--cell",
    },
    {
      field: "loginTime",
      headerName: headerData[3],
      flex: 3,
      cellClassName: "name-column--cell",
    },
    {
      field: "logoutTime",
      headerName: headerData[5],
      flex: 3,
      type: "number",
      headerAlign: "left",
      cellClassName: "name-column--cell",
      align: "left",
      renderCell: (params) => (
        <Typography variant="body2" color="textPrimary" fontSize={"15px"}>
          {params.row.logoutTime === null
            ? "Still Working"
            : params.row.logoutTime}
        </Typography>
      ),
    },
    {
      field: "action",
      headerName: "Status",
      flex: 2,
      headerAlign: "left",
      align: "left",
      renderCell: (params) => (
        <Box>
          <Button
            onClick={() => handleUpdate(params.id)}
            variant="contained"
            style={{
              backgroundColor:
                params.row.logoutTime != null ? "#d84705" : "#4cceac",
              color: "black",
            }}
            disabled={params.row.logoutTime != null}
          >
            {params.row.logoutTime != null ? "Logged Out" : "Logout"}
          </Button>
        </Box>
      ),
    },
  ];
  return (
    <Box m="0 20px">
      <Button
      onClick={handleAllLogout}
        sx={{
          position: "absolute",
          background: "red",
          right: 20,
          marginTop: "-10px",
          color: "white",
          fontSize: "15px",
          width: "110px",
          "&:hover": {
            background: "#d86100", // Change background color on hover
            cursor: "pointer", // Change cursor to pointer on hover
          },
        }}
      >
        Logout all
      </Button>
      {isLoading ? (
        <Loader />
      ) : (
        <Box
          display="grid"
          height="78vh"
          sx={{
            "& .MuiDataGrid-root": {
              border: "none",
            },
            "& .MuiDataGrid-cell": {
              borderBottom: "none",
              fontSize: "15px",
            },
            "& .name-column--cell": {
              color: colors.primary[100],
            },
            "& .MuiDataGrid-columnHeaders": {
              backgroundColor: colors.blueAccent[700],
              border: "none",
            },
            "& .MuiDataGrid-virtualScroller": {
              backgroundColor: colors.primary[400],
            },
            "& .MuiDataGrid-footerContainer": {
              borderTop: "none",
              backgroundColor: colors.blueAccent[700],
            },
            "& .MuiCheckbox-root": {
              color: `${colors.greenAccent[200]} !important`,
            },
            "& .MuiDataGrid-toolbarContainer .MuiButton-text": {
              color: `${colors.redAccent[100]} !important`,
            },
          }}
        >
          <DataGrid
                  rows={data}
                  getRowId={(row) => row.attendanceCode}
                  columns={columns}
                  paginationMode="server"
                  rowCount={rowCount}
                  pageSizeOptions={pageSizeOptions}
                  paginationModel={paginationModel}
                  onPaginationModelChange={handlePaginationModelChange}
                  // loading={isLoading}
                  components={{ Toolbar: CustomGridToolbar }}
                  componentsProps={{
                    toolbar: {
                      onExport: handleExportCSV,
                    },
                  }}
                />
        </Box>
      )}
    </Box>
  );
};

export default DailyStudentOverview;
